import { ReactComponent as AirtableIcon } from 'assets/icons/logos/airtable16.svg'
import { ReactComponent as CanvaIcon } from 'assets/icons/logos/canva16.svg'
import { ReactComponent as DropboxIcon } from 'assets/icons/logos/dropbox16.svg'
import { ReactComponent as FigmaIcon } from 'assets/icons/logos/figma16.svg'
import { ReactComponent as GoogleDocsIcon } from 'assets/icons/logos/googleDocs16.svg'
import { ReactComponent as GoogleDriveIcon } from 'assets/icons/logos/googleDrive16.svg'
import { ReactComponent as GoogleSheetsIcon } from 'assets/icons/logos/googleSheets16.svg'
import { ReactComponent as GoogleSlidesIcon } from 'assets/icons/logos/googleSlides16.svg'
import { ReactComponent as ImagesIcon } from 'assets/icons/logos/images16.svg'
import { ReactComponent as PDFIcon } from 'assets/icons/logos/pdf16.svg'
import { REACT_APP_BLAZE } from 'env'

import type { InformalFeedbackRequestArticle } from 'api/clients/InformalFeedbackRequestsClient'
import type { TodoDocument } from 'api/clients/TodoClient'
import { SourceType } from 'components/ImportModal/Types'
import type {
  ArticleFlat,
  ArticleMeta,
  ArticleTodosGroupArticle,
  Doc,
  AlmanacFile as File,
  HandbookItemDocument,
  MentionableArticle,
} from 'types'
export const LOADING_TITLE = 'Extracting file name...'

export const linkBottomLabel = () => {
  return (
    <>
      Supported Links: <AirtableIcon /> Airtable <CanvaIcon /> Canva <GoogleDocsIcon /> Docs <GoogleSheetsIcon /> Sheets
      <GoogleSlidesIcon /> Slides <FigmaIcon /> Figma <PDFIcon /> PDF <ImagesIcon /> Images
    </>
  )
}

export enum FileType {
  AIRTABLE = 'airtable',
  CANVA = 'canva',
  GOOGLE = 'google',
  DOCUMENT = 'document',
  SPREADSHEETS = 'spreadsheets',
  PRESENTATION = 'presentation',
  FILE = 'file', // google drive
  FIGMA = 'figma',
  DROPBOX = 'dropbox',
  PDF = 'pdf',
  IMAGES = 'images',
}

export type GoogleFileType = FileType.DOCUMENT | FileType.SPREADSHEETS | FileType.PRESENTATION | FileType.FILE
export type ArticleDocType =
  | InformalFeedbackRequestArticle
  | ArticleFlat
  | ArticleMeta
  | MentionableArticle
  | File
  | Doc
  | TodoDocument
  | HandbookItemDocument
  | ArticleTodosGroupArticle

export const FILE_NAME: Record<FileType, string> = {
  [FileType.AIRTABLE]: 'Airtable',
  [FileType.CANVA]: 'Canva',
  [FileType.FIGMA]: 'Figma',
  [FileType.GOOGLE]: 'Google',
  [FileType.DOCUMENT]: 'Google Docs',
  [FileType.SPREADSHEETS]: 'Google Sheets',
  [FileType.PRESENTATION]: 'Google Slides',
  [FileType.FILE]: 'Google Drive',
  [FileType.DROPBOX]: 'Dropbox',
  [FileType.PDF]: 'PDF File',
  [FileType.IMAGES]: 'Image File',
} as const

export const GOOGLE_EMBED_ENDING: Record<GoogleFileType, string> = {
  [FileType.DOCUMENT]: 'pub?embedded=true',
  [FileType.SPREADSHEETS]: 'pubhtml?widget=true&amp;headers=false',
  [FileType.PRESENTATION]: 'embed?start=false&loop=false&delayms=3000',
  [FileType.FILE]: 'preview',
} as const

export const FILE_ICON: Record<FileType, () => ReturnType<typeof AirtableIcon>> = {
  [FileType.AIRTABLE]: () => <AirtableIcon role="img" title="Airtable Icon" />,
  [FileType.CANVA]: () => <CanvaIcon role="img" title="Canva Icon" />,
  [FileType.FIGMA]: () => <FigmaIcon role="img" title="Figma Icon" />,
  [FileType.DOCUMENT]: () => <GoogleDocsIcon role="img" title="Document Icon" />,
  [FileType.GOOGLE]: () => <GoogleDocsIcon role="img" title="Document Icon" />,
  [FileType.SPREADSHEETS]: () => <GoogleSheetsIcon role="img" title="Spreadsheet Icon" />,
  [FileType.PRESENTATION]: () => <GoogleSlidesIcon role="img" title="Presentation Icon" />,
  [FileType.DROPBOX]: () => <DropboxIcon role="img" title="Dropbox Icon" />,
  [FileType.FILE]: () => <GoogleDriveIcon role="img" title="Drive Icon" />,
  [FileType.PDF]: () => <PDFIcon role="img" title="PDF Icon" />,
  [FileType.IMAGES]: () => <ImagesIcon role="img" title="Images Icon" />,
} as const

export const FILE_SCREENSHOT: Record<FileType, string> = {
  [FileType.AIRTABLE]:
    'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1664360692/public/product_education_images/airtableScreenshot_jwxdie.png',
  [FileType.CANVA]:
    'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1664360691/public/product_education_images/canvaScreenshot_iwmlqy.png',
  [FileType.FIGMA]:
    'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1666822018/public/product_education_images/figmaScreenshot_qkpt92.png',
  [FileType.GOOGLE]:
    'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1664360692/public/product_education_images/googleDocsScreenshot_tpamvk.png',
  [FileType.DOCUMENT]:
    'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1664360692/public/product_education_images/googleDocsScreenshot_tpamvk.png',
  [FileType.SPREADSHEETS]:
    'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1664360693/public/product_education_images/googleSheetsScreenshot_fdbpif.png',
  [FileType.PRESENTATION]:
    'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1664360692/public/product_education_images/googleSlidesScreenshot_jibych.png',
  [FileType.FILE]:
    'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1664360692/public/product_education_images/googleDriveScreenshot_ahbxas.png',
  [FileType.DROPBOX]:
    'https://res.cloudinary.com/almanac/image/upload/f_auto,q_auto/v1664360692/public/product_education_images/dropboxScreenshot_re9tes.png',
  [FileType.PDF]: '',
  [FileType.IMAGES]: '',
} as const

export const FILE_SCREENSHOT_TEXT: Record<FileType, string> = {
  [FileType.AIRTABLE]: 'In Airtable, click File -> Share -> Share publicly',
  [FileType.CANVA]: 'In Canva, click File -> Share',
  [FileType.FIGMA]: 'In Figma, click Share -> Get embed code',
  [FileType.GOOGLE]: 'In Google Docs, click File -> Share -> Publish to Web',
  [FileType.DOCUMENT]: 'In Google Docs, click File -> Share -> Publish to Web',
  [FileType.SPREADSHEETS]: 'In Google Sheets, click File -> Share -> Publish to Web',
  [FileType.PRESENTATION]: 'In Google Slides, click File -> Publish to Web',
  [FileType.DROPBOX]: 'In Dropbox, click Share -> Copy link',
  [FileType.FILE]: 'In Google Drive, click Open in new window -> Embed Item...',
  [FileType.PDF]: 'PDF file error',
  [FileType.IMAGES]: 'Images file error',
} as const

const blazeFlag = localStorage.getItem('blaze-ci')
const isBlaze = REACT_APP_BLAZE === true || !!blazeFlag

export const HELP_LINK: Record<FileType, string> = {
  [FileType.AIRTABLE]: isBlaze
    ? 'https://blazehelpcenter.almanac.io/folders/blaze-help-center-M8z9eD/reviews-on-external-files-BaQH44NEFoqNffekb6ooW9rcx04JiBBR#5d7840d1-e28b-49a9-b8bd-ea8ab0ca0d25'
    : 'https://helpcenter.almanac.io/docs/6ydLvMXP7AP2MurJDb1ZNZ7hall6ynOH#a7ba6711-44c5-4df3-a1af-7f97c11d7755',
  [FileType.CANVA]: isBlaze
    ? 'https://blazehelpcenter.almanac.io/folders/blaze-help-center-M8z9eD/reviews-on-external-files-BaQH44NEFoqNffekb6ooW9rcx04JiBBR#cbf172ee-0ea7-4e0b-a0b3-f3638d714403'
    : 'https://helpcenter.almanac.io/docs/6ydLvMXP7AP2MurJDb1ZNZ7hall6ynOH#77024e8f-e994-4d30-b6a9-804c9b6a7dae',
  [FileType.FIGMA]: isBlaze
    ? 'https://blazehelpcenter.almanac.io/folders/blaze-help-center-M8z9eD/reviews-on-external-files-BaQH44NEFoqNffekb6ooW9rcx04JiBBR#9616ffdd-062a-40f8-b069-c8caed00ed1a'
    : 'https://helpcenter.almanac.io/docs/6ydLvMXP7AP2MurJDb1ZNZ7hall6ynOH#c7696c31-e410-43c9-bada-60254884313a',
  [FileType.GOOGLE]: isBlaze
    ? 'https://blazehelpcenter.almanac.io/folders/blaze-help-center-M8z9eD/reviews-on-external-files-BaQH44NEFoqNffekb6ooW9rcx04JiBBR#c4bf7d43-8c57-453d-afc3-587ee54609c5'
    : 'https://helpcenter.almanac.io/docs/6ydLvMXP7AP2MurJDb1ZNZ7hall6ynOH#6e20db7c-5916-4674-a0e7-bd4d48661b5d',
  [FileType.DOCUMENT]: isBlaze
    ? 'https://blazehelpcenter.almanac.io/folders/blaze-help-center-M8z9eD/reviews-on-external-files-BaQH44NEFoqNffekb6ooW9rcx04JiBBR#c4bf7d43-8c57-453d-afc3-587ee54609c5'
    : 'https://helpcenter.almanac.io/docs/6ydLvMXP7AP2MurJDb1ZNZ7hall6ynOH#6e20db7c-5916-4674-a0e7-bd4d48661b5d',
  [FileType.SPREADSHEETS]: isBlaze
    ? 'https://blazehelpcenter.almanac.io/folders/blaze-help-center-M8z9eD/reviews-on-external-files-BaQH44NEFoqNffekb6ooW9rcx04JiBBR#61820ee9-2574-4e3a-9dd3-3e22ac11023c'
    : 'https://helpcenter.almanac.io/docs/6ydLvMXP7AP2MurJDb1ZNZ7hall6ynOH#3f1f52b8-48f9-4e17-8a5f-62b84fbb2858',
  [FileType.PRESENTATION]: isBlaze
    ? 'https://blazehelpcenter.almanac.io/folders/blaze-help-center-M8z9eD/reviews-on-external-files-BaQH44NEFoqNffekb6ooW9rcx04JiBBR#85b7dbd5-52b5-45ff-8e02-cefe4f4c5699'
    : 'https://helpcenter.almanac.io/docs/6ydLvMXP7AP2MurJDb1ZNZ7hall6ynOH#09ce6b27-4535-414d-90d2-0a75012ab6a4',
  [FileType.DROPBOX]: isBlaze
    ? 'https://blazehelpcenter.almanac.io/folders/blaze-help-center-M8z9eD/reviews-on-external-files-BaQH44NEFoqNffekb6ooW9rcx04JiBBR#8a0c85c9-0d68-43ae-9b96-7f04fa984c22'
    : 'https://helpcenter.almanac.io/docs/6ydLvMXP7AP2MurJDb1ZNZ7hall6ynOH#5e2ddea0-ebbe-4f7a-88c6-b002d4cee0f2',
  [FileType.FILE]: isBlaze
    ? 'https://blazehelpcenter.almanac.io/folders/blaze-help-center-M8z9eD/reviews-on-external-files-BaQH44NEFoqNffekb6ooW9rcx04JiBBR#40066b41-8556-4f12-b58d-9c90e3dde5ca'
    : 'https://helpcenter.almanac.io/docs/6ydLvMXP7AP2MurJDb1ZNZ7hall6ynOH#79554a0e-5822-4f0e-a938-00864fdc4441',
  [FileType.PDF]: '',
  [FileType.IMAGES]: '',
} as const

export interface ValidationResult {
  validUrl: string
  isEmbeddable: boolean
  newFileType?: FileType
  newFileSubType?: FileType
}

export const emptyValidationResponse = {
  validUrl: '',
  isEmbeddable: false,
  newFileType: undefined,
}

export const getFileTypeFrom = (url: string) => {
  let regexGroups = /(?:https:\/\/)?(?:www.)?([\w\-_]+).com(?:\/s\/.*(pdf|png|jpeg|jpg))?/.exec(url)
  if (regexGroups && Object.values(FileType).includes(regexGroups?.[1] as FileType)) {
    return regexGroups[1] as FileType
  } else {
    // Check for PDF hosted anywhere
    let regexGroups = /(?:https:\/\/)?(?:www.)?.*\.(pdf)/.exec(url)
    if (regexGroups?.[1] && regexGroups[1] === FileType.PDF) return regexGroups[1] as FileType
  }

  return null
}

export const getSourceTypeFrom = (url: string) => {
  const embeddableSourceTypes = [SourceType.Canva, SourceType.Figma, SourceType.Airtable]
  let regexGroups = /(?:https:\/\/)?(?:www.)?([\w\-_]+).com(?:\/s\/.*(pdf|png|jpeg|jpg))?/.exec(url)
  const domain = regexGroups?.[1]
  if (regexGroups && domain === 'google') {
    if (url.includes('/spreadsheets')) return SourceType.GoogleSheets
    if (url.includes('/presentation')) return SourceType.GoogleSlides
  }

  if (regexGroups && Object.values(embeddableSourceTypes).includes(domain as SourceType)) {
    return domain as SourceType
  }

  return null
}

export const externalFileEmoji = (asset: { external_file_icon?: string | null }) => {
  if (!asset || !asset.external_file_icon) return null

  return FILE_ICON[asset.external_file_icon as FileType]?.()
}
